import './heat-legend.scss';
import { IonCol, IonRow } from '@ionic/react';
import { heatLegendAllInsight, heatLegendJackpots, isJackpotInsight } from 'common/common';
import { floormapLegendInactiveColors } from 'common/floormapHeat';
import { InsightValue } from '../../../../common/types';
import { config } from '../../../../config/config';
import React from 'react';
import { floormapHeatBeanRatings, floormapJackpotHeatBeanRatings } from 'config/HeatBeanRatings';
import {
  MAP_OVERLAY_ROOT_ATTR,
  useMapOverlayExpandCollapse,
  type MapOverlayMapLike,
} from 'hooks/useMapOverlayExpandCollapse';

export enum DirectionType {
  horizontal = 'horizontal',
  vertical = 'vertical',
}

const inactiveLegendColor = (heatLevel: number): string =>
  floormapLegendInactiveColors[heatLevel as keyof typeof floormapLegendInactiveColors] ??
  '#464d55';

interface HeatLegendProps {
  onClick: (heatLevel: number) => void;
  direction: DirectionType;
  selectedInsight: InsightValue;
  heatFilter: number[];
  // When provided, renders a rising-sun toggle at the top of the vertical ledge.
  // Clicking when narrowed selects all heat colors; clicking when all are selected
  // narrows to 2 colors (top 2, or bottom 2 when coldFocus).
  onSelectHeatLevels?: (heatLevels: number[]) => void;
  // When true (the Coldest view), the sun click narrows to the bottom 2
  // colors instead of the top 2.
  coldFocus?: boolean;
  // Mapbox map instance — used to collapse the expanded legend on drag start.
  map?: MapOverlayMapLike | null;
}

/** Rising-sun icon from Figma map color key (node light / 12165:65069), oriented flat-bottom / rays-up. */
const RisingSunIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 29.0075 15.3659"
    fill="none"
    className={className}
    aria-hidden="true"
  >
    <g transform="matrix(1 0 0 -1 0 15.3659)">
      <path
        d="M0.727273 0.862948L4.48739 0.862949"
        stroke="currentColor"
        strokeWidth="1.45455"
        strokeLinecap="round"
      />
      <path
        d="M4.66134 10.8183L7.32015 8.1595"
        stroke="currentColor"
        strokeWidth="1.45455"
        strokeLinecap="round"
      />
      <path
        d="M14.5046 14.6386L14.5046 10.8785"
        stroke="currentColor"
        strokeWidth="1.45455"
        strokeLinecap="round"
      />
      <path
        d="M28.2802 0.862701L24.5201 0.8627"
        stroke="currentColor"
        strokeWidth="1.45455"
        strokeLinecap="round"
      />
      <path
        d="M24.4594 10.8183L21.8006 8.1595"
        stroke="currentColor"
        strokeWidth="1.45455"
        strokeLinecap="round"
      />
      <path
        d="M7.77667 0.727272C7.77667 4.44276 10.7887 7.45476 14.5042 7.45476C18.2196 7.45476 21.2316 4.44276 21.2316 0.727273"
        stroke="currentColor"
        strokeWidth="1.45455"
        strokeLinecap="round"
      />
    </g>
  </svg>
);

function HorizontalEachItem({ eachHeat, selectedInsight, heat, color }: any) {
  return (
    <>
      {heat.is_highest &&
        (selectedInsight.indexOf('volatility_rating') === 0 ? (
          <td className="heat-legend__label">{heat.label_volatility}</td>
        ) : (
          <td className="heat-legend__label">{heat.label}</td>
        ))}
      <td style={{ paddingRight: 2 }}>
        <div className="heat-legend-chart__color" style={{ background: color }}></div>
      </td>
      {eachHeat === 1 &&
        (selectedInsight.indexOf('volatility_rating') === 0 ? (
          <td className="heat-legend__label">{heat.label_volatility}</td>
        ) : (
          <td className="heat-legend__label">{heat.label}</td>
        ))}
    </>
  );
}

const HeatLegend: React.FC<HeatLegendProps> = ({
  onClick,
  direction,
  selectedInsight,
  heatFilter,
  onSelectHeatLevels,
  coldFocus = false,
  map = null,
}) => {
  const isJackpot = isJackpotInsight(selectedInsight);
  const ratings = isJackpot ? floormapJackpotHeatBeanRatings : floormapHeatBeanRatings;
  // Key count (3 vs 5) is structural — jackpot insights only use the top 3 heat tiers
  // regardless of whether the gold-color feature flag is on or off.
  const isJackpotKeys = selectedInsight === 'jackpots' || selectedInsight === 'jackpot_value';
  const visibleLegendKeys = [...(isJackpotKeys ? heatLegendJackpots : heatLegendAllInsight)].sort(
    (left, right) => right - left
  );
  const highestVisibleHeat = visibleLegendKeys[0];
  const heatLegend = visibleLegendKeys.reduce((hash: Record<number, any>, heatLevel) => {
    hash[heatLevel] = {
      ...ratings[heatLevel],
      is_highest: heatLevel === highestVisibleHeat,
    };
    return hash;
  }, {});

  const selectedOption: any = config.slotInsights.find((option: any) => {
    return option.value === selectedInsight;
  });

  // Collapsed by default (slim PR #13 size). Tap expands to PR #14 size for interaction.
  const { expanded, rootRef, expand, markInteraction, rootProps } = useMapOverlayExpandCollapse({
    map,
    collapsedAriaLabel: 'Expand heat map legend',
    expandedAriaLabel: 'Heat map legend',
  });

  // Sun toggle (vertical ledge). Keys are sorted highest-first, so the first two
  // are the "top 2" (hottest) and the last two are the "bottom 2" (coldest).
  const selectableKeys = visibleLegendKeys.filter((heatLevel) => heatLegend[heatLevel].is_show);
  const allHeatSelected =
    heatFilter.length === 0 || selectableKeys.every((heatLevel) => heatFilter.includes(heatLevel));
  const handleSunClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expanded) {
      expand();
      return;
    }
    if (!onSelectHeatLevels) return;
    markInteraction();
    if (!allHeatSelected) {
      onSelectHeatLevels([...selectableKeys]);
      return;
    }
    // All selected → narrow to 2. The Coldest view focuses the bottom 2 colors;
    // everywhere else focuses the top 2.
    onSelectHeatLevels(coldFocus ? selectableKeys.slice(-2) : selectableKeys.slice(0, 2));
  };

  const handleTileClick = (heatLevel: number) => (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!expanded) {
      expand();
      return;
    }
    markInteraction();
    onClick(heatLevel);
  };

  const HorizontalRow = () => {
    return (
      <tr>
        {visibleLegendKeys.map(
          (eachHeat: any) =>
            heatLegend[eachHeat].is_show === true && (
              <HorizontalEachItem
                key={eachHeat}
                eachHeat={eachHeat}
                color={heatLegend[eachHeat].color}
                heat={heatLegend[eachHeat]}
                selectedInsight={selectedInsight}
              />
            )
        )}
      </tr>
    );
  };

  const verticalTiles = visibleLegendKeys.map((eachHeat: any) => {
    if (heatLegend[eachHeat].is_show !== true || direction !== DirectionType.vertical) {
      return null;
    }
    const isActive = heatFilter.indexOf(Number(eachHeat)) !== -1 || heatFilter.length === 0;
    return (
      <div
        key={eachHeat}
        className={[
          isActive ? 'heat-legend__active' : 'heat-legend__inactive',
          'heat-legend__color-square',
        ]
          .filter(Boolean)
          .join(' ')}
        style={{
          background: isActive
            ? heatLegend[eachHeat].color
            : inactiveLegendColor(Number(eachHeat)),
        }}
        onClick={handleTileClick(parseInt(eachHeat, 10))}
        role={expanded ? 'button' : undefined}
        tabIndex={expanded ? 0 : -1}
        aria-hidden={!expanded}
        onKeyDown={
          expanded
            ? (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  handleTileClick(parseInt(eachHeat, 10))(event as unknown as React.MouseEvent);
                }
              }
            : undefined
        }
      />
    );
  });

  if (direction === DirectionType.vertical) {
    const className = [
      'heat-legend',
      'heat-legend--vertical',
      expanded ? 'heat-legend--expanded' : 'heat-legend--collapsed',
    ].join(' ');

    // Keep a single root element across expand/collapse so width/tile CSS
    // transitions can run (remounting button↔div would snap with no animation).
    // Collapsed: sun is a non-nested control that still calls handleSunClick so
    // the first tap expands (same tap-to-grow as a color tile). Expanded: real
    // button runs show-all / narrow filter and resets the idle timer.
    const sunButton = onSelectHeatLevels ? (
      expanded ? (
        <button
          type="button"
          className={`heat-legend__sun${allHeatSelected ? ' heat-legend__sun--all' : ' heat-legend__sun--narrow'}`}
          aria-label={
            allHeatSelected
              ? coldFocus
                ? 'Show bottom 2 heat colors'
                : 'Show top 2 heat colors'
              : 'Show all heat colors'
          }
          onClick={handleSunClick}
        >
          <RisingSunIcon className="heat-legend__sun-icon" />
        </button>
      ) : (
        <span
          className={`heat-legend__sun${allHeatSelected ? ' heat-legend__sun--all' : ' heat-legend__sun--narrow'}`}
          aria-hidden="true"
          onClick={handleSunClick}
        >
          <RisingSunIcon className="heat-legend__sun-icon" />
        </span>
      )
    ) : null;

    return (
      <div
        ref={rootRef as React.RefObject<HTMLDivElement>}
        className={className}
        {...{ [MAP_OVERLAY_ROOT_ATTR]: '' }}
        {...rootProps}
      >
        {sunButton}
        <div className="heat-legend__column">{verticalTiles}</div>
      </div>
    );
  }

  return (
    <div className="heat-legend-chart">
      <IonRow className="ion-align-items-center">
        <IonCol>
          <div className="heat-legend">
            <HorizontalRow />
          </div>
        </IonCol>
        {(selectedOption.value === 'return' || selectedOption.value === 'win_rate') && (
          <IonCol className="ion-padding-start">
            <table>
              <tr>
                <td>
                  <div
                    className="heat-legend__floor-avg-vertical"
                    style={{ background: selectedOption.backgroundColor }}
                  ></div>
                </td>
                <td className="heat-legend__floor-label">Machine Average</td>
              </tr>
            </table>
          </IonCol>
        )}
      </IonRow>
    </div>
  );
};

export default HeatLegend;
